// src/components/pages/SocialVideo.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import {
  getUserProfileFromLocalStorage,
  getArtistProfile,
} from "../../services/profileService";
import { useTheme } from "styled-components";
import { Box, Card, Typography, Container, Button } from "@mui/material";

const SocialVideo = () => {
  const theme = useTheme();
  const navigate = useNavigate(); // Initialize useNavigate
  const [username, setUsername] = useState("");
  const [filmItems, setFilmItems] = useState([]);
  const [imageErrors, setImageErrors] = useState({}); // Track images with errors
  const [loading, setLoading] = useState(true); // Track loading state
  const [error, setError] = useState(null); // Track error state

  useEffect(() => {
    const userProfile = getUserProfileFromLocalStorage();

    if (userProfile) {
      setUsername(userProfile.userName);
    } else {
      setLoading(false); // No user profile found
    }
  }, []);

  useEffect(() => {
    const fetchArtistProfile = async () => {
      if (username) {
        try {
          const artistProfile = await getArtistProfile(username);
          const studioItems = artistProfile?.studio || [];
          const filteredItems = studioItems.filter(
            (item) => item.art?.medium === "Film"
          );

          setFilmItems(filteredItems);
        } catch (error) {
          console.error(
            "Error fetching artist profile or adding video:",
            error
          );
          setError("Failed to load social videos. Please try again later.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchArtistProfile();
  }, [username]);

  const handleImageError = (artworkId) => {
    setImageErrors((prev) => ({ ...prev, [artworkId]: true })); // Mark image as failed
  };

  const handleButtonClick = (videoId, saleId) => {
    navigate(`/challenge/videos/${videoId}/interactions`, {
      state: { saleId },
    }); // Navigate to the URL
  };

  if (loading) {
    return (
      <Container>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontFamily: "basic-sans" }}
        >
          Social Video
        </Typography>
        <Typography variant="body1">Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontFamily: "basic-sans" }}
        >
          Social Video
        </Typography>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontFamily: "basic-sans" }}
      >
        Social Video
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filmItems.map((item, index) => (
          <Card
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 2,
              gap: 2,
              backgroundColor: theme.colors.mediumPurple,
              flexDirection: {
                xs: "column", // Stack vertically on small screens
                sm: "row", // Default to horizontal layout on medium+ screens
              },
              textAlign: {
                xs: "center", // Center-align text on small screens
                sm: "left", // Default to left-align text on medium+ screens
              },
            }}
          >
            {/* First column: Image or fallback box */}
            <Box
              sx={{
                flex: {
                  xs: "0 0 auto", // Allow natural size on small screens
                  sm: "0 0 auto", // Fixed size for medium+ screens
                },
                width: {
                  xs: "100%", // Full width on small screens
                  sm: "160px", // Fixed width on medium+ screens
                },
                position: "relative", // Required for the aspect ratio trick
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: imageErrors[item.art.artworkId]
                  ? theme.colors.darkPurple // Fallback color
                  : "transparent",
              }}
            >
              {/* Maintain aspect ratio */}
              <Box
                sx={{
                  paddingTop: "56.25%", // 16:9 aspect ratio (9/16 * 100)
                  position: "relative",
                }}
              >
                {imageErrors[item.art.artworkId] ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: theme.colors.darkPurple,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.colors.palePurple }}
                    >
                      No Image
                    </Typography>
                  </Box>
                ) : (
                  <img
                    src={`https://swayer.blob.core.windows.net/${item.art.artworkId}-pub/thumbnail_master.png`}
                    alt={item.art.title || "Artwork"}
                    onError={() => handleImageError(item.art.artworkId)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Second column: Title and description */}
            <Box
              sx={{
                flex: "1",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                textAlign: {
                  xs: "center", // Center-align on small screens
                  sm: "left", // Default to left-align on medium+ screens
                },
              }}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontFamily: "basic-sans",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                {item.art.title || "Untitled"}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontFamily: "basic-sans", color: "white" }}
              >
                {item.art.description || "No description available."}
              </Typography>
            </Box>

            {/* Third column: Button */}
            <Box
              sx={{
                flex: {
                  xs: "0 0 auto", // Allow natural size on small screens
                  sm: "0 0 auto", // Fixed size for medium+ screens
                },
                display: "flex",
                alignItems: "center",
                justifyContent: {
                  xs: "center", // Center-align button on small screens
                  sm: "flex-start", // Default alignment on medium+ screens
                },
                width: {
                  xs: "100%", // Full width on small screens
                  sm: "auto", // Natural size on medium+ screens
                },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  handleButtonClick(item.art.artworkId, item.art.sales[0])
                } // Pass videoId and saleId on click
                sx={{
                  backgroundColor: theme.colors.green,
                  padding: "10px 20px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: "50px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.colors.blue,
                    boxShadow: "none",
                  },
                  width: {
                    xs: "100%", // Full width on small screens
                    sm: "auto", // Default size on medium+ screens
                  },
                }}
              >
                Add Interactive Elements
              </Button>
            </Box>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default SocialVideo;
